/*
 * ANATE-PERF R8: sibling-swatch behavior for product cards, extracted from
 * snippets/product-grid-item-gemini2.liquid where every card carried its own
 * identical ~210-line inline <script> (24x parse/execute on a full grid).
 *
 * Uses document-level event delegation, so it also works for cards injected
 * later via AJAX (related products, recently viewed) -- those cards' inline
 * scripts never executed at all before (innerHTML doesn't run <script> tags),
 * meaning AJAX-loaded cards gain working swatches with this change.
 *
 * Per-card state (original images/text/link, current selection) lives in a
 * WeakMap keyed by the card element and is initialized lazily on first
 * interaction, before anything has been modified.
 */
(function () {
  'use strict';

  if (window._gridItemSiblingsGemini2Loaded) return;
  window._gridItemSiblingsGemini2Loaded = true;

  // Global cache shared across all product cards (kept from the inline version)
  if (!window.__siblingCache) window.__siblingCache = new Map();
  const cache = window.__siblingCache;

  const cardState = new WeakMap();

  function getState(card) {
    let state = cardState.get(card);
    if (state) return state;

    const imageContainer = card.querySelector('.product-item__bg');
    // Walk the SLIDES (not just their imgs): a slide can hold an <img> or a
    // deferred hover <video>. Index-mapping by slide keeps sibling media
    // aligned even when a video sits between images.
    const slides = imageContainer
      ? Array.from(imageContainer.querySelectorAll('.product-item__bg__slide')).map((el) => {
          const img = el.querySelector('img');
          const video = el.querySelector('video[data-hover-video]');
          return {
            el,
            img,
            video,
            originalImg: img ? { src: img.src, srcset: img.srcset || '' } : null,
            originalVideo: video
              ? {
                  poster: video.getAttribute('poster') || '',
                  sources: Array.from(video.querySelectorAll('source')).map((s) => ({
                    url: s.getAttribute('src') || s.getAttribute('data-src') || '',
                    type: s.getAttribute('type') || '',
                  })),
                }
              : null,
          };
        })
      : [];
    const mainFigureImg = imageContainer ? imageContainer.querySelector(':scope > figure img') : null;
    const mainVideo = imageContainer ? imageContainer.querySelector(':scope > video[data-card-main-video]') : null;
    const metalEl = card.querySelector('.anate-metal p');
    const priceEl = card.querySelector('.anate-price');
    const productLinks = Array.from(card.querySelectorAll('.product-link'));
    const selectedSibling = card.querySelector('.gemini2-swatch.is-current');

    state = {
      imageContainer,
      slides,
      mainFigureImg,
      mainVideo,
      metalEl,
      priceEl,
      productLinks,
      originalMainImage: mainFigureImg ? { src: mainFigureImg.src, srcset: mainFigureImg.srcset || '' } : null,
      originalMainVideo: mainVideo
        ? {
            poster: mainVideo.getAttribute('poster') || '',
            sources: Array.from(mainVideo.querySelectorAll('source')).map((s) => ({
              url: s.getAttribute('src') || s.getAttribute('data-src') || '',
              type: s.getAttribute('type') || '',
            })),
          }
        : null,
      original: {
        metal: metalEl ? metalEl.textContent : '',
        price: priceEl ? priceEl.innerHTML : '',
        link: productLinks[0] ? productLinks[0].href : '',
      },
      selectedSibling,
      currentHandle: selectedSibling ? selectedSibling.dataset.siblingHandle : null,
      hoveredSwatch: null,
    };
    cardState.set(card, state);

    // One mouseleave handler per card, attached on first interaction:
    // revert to the selected sibling (or the original product) when leaving.
    card.addEventListener('mouseleave', () => {
      state.hoveredSwatch = null;
      if (!state.selectedSibling) return;
      if (state.selectedSibling.dataset.siblingHandle === card.dataset.productHandle) {
        revertToOriginal(card, state);
      } else {
        updateCard(card, state, state.selectedSibling);
      }
    });

    return state;
  }

  // Fetched from /products/{handle}.js (not .json): the .js endpoint includes the
  // full media array -- images AND videos with their mp4 sources + poster -- so
  // sibling swaps can also update the deferred hover video, not just images.
  async function prefetchSibling(handle) {
    if (!handle || cache.has(handle)) return;
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return;
      const product = await res.json();
      const media = (product.media || []).map((m) => {
        const preview = (m.preview_image && m.preview_image.src) || '';
        if (m.media_type === 'video') {
          return {
            type: 'video',
            image: preview,
            poster: preview,
            sources: (m.sources || [])
              .filter((s) => (s.format || '').toLowerCase() === 'mp4' || String(s.mime_type || '').includes('mp4'))
              .map((s) => ({ url: s.url, type: s.mime_type || 'video/mp4' })),
          };
        }
        // images / external_video / model all render via their preview image on cards
        return { type: 'image', image: m.src || preview };
      });
      cache.set(handle, {
        media,
        title: product.title,
        handle: product.handle,
      });
    } catch (e) {
      // Silently fail - swatch will still navigate on click via the link swap
    }
  }

  // Swap a deferred hover video's poster + sources. Sources go into data-src
  // (the pattern card-hover-video-gemini2.js expects), so the new video only
  // downloads on actual hover/visibility intent, same as the original.
  function withWidth(url, w) {
    if (!url || url.indexOf('width=') !== -1) return url;
    return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'width=' + w;
  }

  function setVideoMedia(video, poster, sources) {
    try { video.pause(); } catch (e) { /* not playing */ }
    delete video.dataset.sourcesLoaded;
    video.removeAttribute('src');
    if (poster) video.setAttribute('poster', withWidth(poster, 640));
    video.querySelectorAll('source').forEach((s) => s.remove());
    (sources || []).forEach((src) => {
      if (!src.url) return;
      const s = document.createElement('source');
      s.setAttribute('data-src', src.url);
      if (src.type) s.setAttribute('type', src.type);
      video.appendChild(s);
    });
    video.load(); // refreshes the displayed poster frame
  }

  // Immediately load + play a facade video (used for the desktop main autoplay
  // video after a sibling swap -- its IntersectionObserver won't re-fire because
  // the element never left the viewport).
  function loadAndPlay(video) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    video.querySelectorAll('source[data-src]').forEach((s) => {
      s.setAttribute('src', s.getAttribute('data-src'));
      s.removeAttribute('data-src');
    });
    video.dataset.sourcesLoaded = 'true';
    video.load();
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  }

  // ANATE-PERF R12: keep responsive loading when swapping sibling images.
  // product.json returns master CDN URLs; the Shopify CDN accepts a width
  // query param, so we rebuild a srcset instead of forcing the full-size file.
  // Only applied when the <img> has a sizes attribute (otherwise w-descriptors
  // would default to 100vw and over-fetch).
  const SRCSET_WIDTHS = [180, 360, 540, 720, 900, 1080];

  function applySiblingImage(img, url) {
    if (img.getAttribute('sizes')) {
      const sep = url.includes('?') ? '&' : '?';
      img.srcset = SRCSET_WIDTHS.map((w) => url + sep + 'width=' + w + ' ' + w + 'w').join(', ');
    } else {
      img.srcset = '';
    }
    img.src = url;
  }

  function updateCard(card, state, swatch) {
    const handle = swatch.dataset.siblingHandle;
    const siblingMetal = swatch.dataset.siblingMetal;
    const siblingPrice = swatch.dataset.siblingPrice;
    const siblingComparePrice = swatch.dataset.siblingComparePrice;
    const siblingPriceHtml = swatch.dataset.siblingPriceHtml;

    if (state.metalEl && siblingMetal) {
      state.metalEl.textContent = siblingMetal;
    }

    if (state.priceEl) {
      if (siblingPriceHtml) {
        state.priceEl.innerHTML = siblingPriceHtml;
      } else if (siblingPrice) {
        const priceSpan = document.createElement('span');
        priceSpan.className = siblingComparePrice ? 'price sale' : 'price';
        priceSpan.textContent = siblingPrice;
        state.priceEl.replaceChildren(priceSpan);
        if (siblingComparePrice) {
          const compareWrap = document.createElement('span');
          compareWrap.className = 'compare-at';
          const compareStrike = document.createElement('s');
          compareStrike.textContent = siblingComparePrice;
          compareWrap.appendChild(compareStrike);
          state.priceEl.append(document.createTextNode(' '), compareWrap);
        }
      }
    }

    const cached = cache.get(handle);
    if (cached && cached.media.length > 0) {
      const firstVideo = cached.media.find((m) => m.type === 'video');

      state.slides.forEach((slide, i) => {
        const media = cached.media[i];
        if (slide.video) {
          // The card's single deferred hover video: swap in the sibling's video
          // (its first one, wherever it sits), or fall back to a still image
          // shown as the poster when the sibling has no video at all.
          if (firstVideo && firstVideo.sources.length > 0) {
            setVideoMedia(slide.video, firstVideo.poster, firstVideo.sources);
            // If the card is being hovered right now, the hover-video facade
            // won't re-fire (same element), so resume playback ourselves.
            // offsetParent check: skip when the slide is display:none (video-first
            // cards on desktop autoplay via [data-card-main-video] instead).
            if (card.matches(':hover') && window.matchMedia('(hover: hover)').matches && slide.video.offsetParent !== null) {
              slide.video.querySelectorAll('source[data-src]').forEach((s) => {
                s.setAttribute('src', s.getAttribute('data-src'));
                s.removeAttribute('data-src');
              });
              slide.video.dataset.sourcesLoaded = 'true';
              slide.video.load();
              const p = slide.video.play();
              if (p && p.catch) p.catch(() => {});
            }
          } else if (media && media.image) {
            setVideoMedia(slide.video, media.image, []);
          }
        } else if (slide.img && media && media.image) {
          applySiblingImage(slide.img, media.image);
        }
      });

      if (state.mainFigureImg && cached.media[0] && cached.media[0].image) {
        applySiblingImage(state.mainFigureImg, cached.media[0].image);
      }

      // Desktop main autoplay video: swap to the sibling's video, or park it on
      // the sibling's first image as a still poster when the sibling has none.
      if (state.mainVideo) {
        if (firstVideo && firstVideo.sources.length > 0) {
          setVideoMedia(state.mainVideo, firstVideo.poster, firstVideo.sources);
          loadAndPlay(state.mainVideo);
        } else if (cached.media[0] && cached.media[0].image) {
          setVideoMedia(state.mainVideo, cached.media[0].image, []);
        }
      }

      state.imageContainer?.classList.remove('is-loading');
    } else if (handle !== state.currentHandle) {
      state.imageContainer?.classList.add('is-loading');
    }
  }

  function revertToOriginal(card, state) {
    if (state.metalEl) state.metalEl.textContent = state.original.metal;
    if (state.priceEl) state.priceEl.innerHTML = state.original.price;

    state.slides.forEach((slide) => {
      if (slide.img && slide.originalImg) {
        slide.img.src = slide.originalImg.src;
        slide.img.srcset = slide.originalImg.srcset;
      }
      if (slide.video && slide.originalVideo) {
        setVideoMedia(slide.video, slide.originalVideo.poster, slide.originalVideo.sources.map((s) => ({ url: s.url, type: s.type })));
      }
    });

    if (state.mainFigureImg && state.originalMainImage) {
      state.mainFigureImg.src = state.originalMainImage.src;
      state.mainFigureImg.srcset = state.originalMainImage.srcset;
    }

    if (state.mainVideo && state.originalMainVideo) {
      setVideoMedia(state.mainVideo, state.originalMainVideo.poster, state.originalMainVideo.sources.map((s) => ({ url: s.url, type: s.type })));
      loadAndPlay(state.mainVideo);
    }

    state.imageContainer?.classList.remove('is-loading');

    state.productLinks.forEach((link) => {
      link.href = state.original.link;
      if (link.dataset.productLink !== undefined) {
        link.dataset.productLink = state.original.link;
      }
    });
  }

  function findSwatch(event) {
    const swatch = event.target.closest?.('[data-sibling-swatch]');
    if (!swatch) return null;
    const card = swatch.closest('.gemini2-card');
    if (!card) return null;
    return { swatch, card };
  }

  // Hover: prefetch the sibling's images and preview the swap
  document.addEventListener('mouseover', (event) => {
    const found = findSwatch(event);
    if (!found) return;
    const state = getState(found.card);
    if (state.hoveredSwatch === found.swatch) return; // moving within the same swatch
    state.hoveredSwatch = found.swatch;
    prefetchSibling(found.swatch.dataset.siblingHandle);
    updateCard(found.card, state, found.swatch);
  });

  // Click/tap: select this sibling and swap the card in place (no navigation),
  // so mobile taps -- which never fire hover -- swap colors instead of leaving.
  document.addEventListener('click', (event) => {
    const found = findSwatch(event);
    if (!found) return;
    const { swatch, card } = found;
    const state = getState(card);
    const handle = swatch.dataset.siblingHandle;

    event.preventDefault();
    event.stopPropagation();

    if (handle === state.currentHandle) return; // already showing this color

    card.querySelectorAll('[data-sibling-swatch]').forEach((s) => {
      s.classList.remove('is-current');
      s.removeAttribute('aria-current');
      const link = s.querySelector('.sibling__link');
      if (link) link.classList.remove('sibling__link--current');
    });
    swatch.classList.add('is-current');
    swatch.setAttribute('aria-current', 'true');
    const thisLink = swatch.querySelector('.sibling__link');
    if (thisLink) thisLink.classList.add('sibling__link--current');

    state.selectedSibling = swatch;
    state.currentHandle = handle;

    // Update product links so tapping the image opens this color
    const newLink = swatch.dataset.siblingLink;
    state.productLinks.forEach((link) => {
      link.href = newLink;
      if (link.dataset.productLink !== undefined) {
        link.dataset.productLink = newLink;
      }
    });

    // Swap text/price immediately; swap images from cache, or fetch them
    // first (showing the loading state) when missing.
    updateCard(card, state, swatch);
    if (!cache.has(handle)) {
      state.imageContainer?.classList.add('is-loading');
      prefetchSibling(handle).then(() => {
        if (state.selectedSibling !== swatch) return; // selection changed meanwhile
        if (cache.has(handle)) {
          updateCard(card, state, swatch);
        } else {
          state.imageContainer?.classList.remove('is-loading');
        }
      });
    }
  });
})();
